import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import ChipTag from '../components/ui/ChipTag';
import DynamicListInput from '../components/ui/DynamicListInput';
import {
  createPackV3,
  updatePackV3,
  getPackV3,
  PACK_TYPES,
  PACK_STATUS,
  VISIBILITY,
} from '../api/knowledgePacksV3';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, EMZI_TYPOGRAPHY, getButtonStyle } from '../styles/emziCI';
import CompletenessChecklist from '../components/forms/CompletenessChecklist';
import EnhancedProductForm from '../components/forms/EnhancedProductForm';
import ProductPackEditor from '../components/ProductPackEditorV4';
import EnhancedPromotionForm from '../components/forms/EnhancedPromotionForm';
import EnhancedDirectionForm from '../components/forms/EnhancedDirectionForm';
import EnhancedValuesForm from '../components/forms/EnhancedValuesForm';
import EnhancedServicesForm from '../components/forms/EnhancedServicesForm';
import EnhancedComplianceForm from '../components/forms/EnhancedComplianceForm';
import EnhancedViralForm from '../components/forms/EnhancedViralForm';
import EnhancedTrendForm from '../components/forms/EnhancedTrendForm';
import EnhancedAmbassadorForm from '../components/forms/EnhancedAmbassadorForm';
import EnhancedHealthForm from '../components/forms/EnhancedHealthForm';
import FormatContentEditor from '../components/FormatContentEditor';
import { validateKnowledgePackForm, getContentValidator } from '../utils/formValidation';
import AIAssistPanel from '../components/AIAssistPanel';
import ControlledInput from '../components/ControlledInput';
import ControlledTextarea from '../components/ControlledTextarea';
import InlinePackCreator from '../components/InlinePackCreator';
import LiveAgentPreview from '../components/LiveAgentPreview';
import { txt } from '../utils/bilingualText';
import UniversalLanguageField from '../components/forms/UniversalLanguageField';
import * as inboxApi from '../api/inbox';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

// Pack Language Configuration
const BILINGUAL_REQUIRED = {
  product: true,      // Customer-facing
  promotion: true,    // Customer-facing
  direction: false,   // Internal strategy (SINGLE LANGUAGE)
  values: true,       // Brand consistency
  services: true,     // Customer-facing
  compliance: true,   // Legal safety (MUST be bilingual)
  viral: false,       // Creative patterns (single language core)
  trend: false,       // Internal intelligence (SINGLE LANGUAGE)
  ambassador: true,   // Speaks to both audiences
  health: true,       // Critical health info
};

// Form Labels Translation
const FORM_LABELS = {
  packType: {
    bm: 'Jenis Pack',
    urban: 'Jenis Pack',
    en: 'Pack Type'
  },
  packName: {
    bm: 'Nama Pack',
    urban: 'Nama Pack',
    en: 'Pack Name'
  },
  description: {
    bm: 'Penerangan',
    urban: 'Deskripsi',
    en: 'Description'
  },
  universalInputNotice: {
    bm: '🌐 Input Universal: Taip dalam mana-mana bahasa (BM, EN, atau campur). AI akan sesuaikan dengan bahasa audience semasa generate content.',
    urban: '🌐 Universal Language Input: Type dalam any language (BM, EN, atau mix). AI akan adapt ikut audience masa generate content.',
    en: '🌐 Universal Language Input: Type in any language (BM, EN, or mix). AI adapts to audience at generation time.'
  },
  packNameHelper: {
    bm: 'Masukkan nama pack dalam bahasa yang anda selesa. Contoh: "Kopi Ala Kazim untuk Diabetes" atau "Ala Kazim Coffee untuk control diabetes"',
    urban: 'Enter nama pack dalam bahasa yang u comfortable. Contoh: "Kopi Ala Kazim untuk Diabetes" atau "Ala Kazim Coffee untuk control diabetes"',
    en: 'Enter your pack name in any language. Example: "Kopi Ala Kazim untuk Diabetes" or "Ala Kazim Coffee for diabetes control"'
  },
  packNamePlaceholder: {
    bm: 'Taip dalam BM, EN, atau campur...',
    urban: 'Type dalam BM, EN, atau mix...',
    en: 'Type in BM, EN, or mix...'
  },
  descriptionHelper: {
    bm: 'Terangkan pack ini secara ringkas. Guna bahasa yang anda selesa (BM/EN/Campur).',
    urban: 'Explain pack ni dalam short. Guna language yang u selesa (BM/EN/Mix).',
    en: 'Briefly describe this pack. Use your natural language (BM/EN/Mix).'
  },
  descriptionPlaceholder: {
    bm: 'Taip dalam BM, EN, atau campur...',
    urban: 'Type dalam BM, EN, atau mix...',
    en: 'Type in BM, EN, or mix...'
  },
  aiAdaptMessage: {
    bm: 'AI akan sesuaikan dengan bahasa audience semasa generate',
    urban: 'AI akan adapt dengan language audience masa generate',
    en: 'AI will adapt to audience language at generation time'
  },
  required: {
    bm: '(wajib)',
    urban: '(required)',
    en: '(required)'
  }
};

// Stable AccordionSection - defined OUTSIDE the main component to prevent remounting on every render
const AccordionSection = React.memo(({ number, title, children, isOpen, onToggle, hasErrors }) => {
  const isMobile = window.innerWidth <= 768;

  return (
    <div 
      style={{
        border: `1px solid ${hasErrors ? EMZI_COLORS.error : EMZI_COLORS.border}`,
        borderRadius: EMZI_RADIUS.md,
        marginBottom: isMobile ? '8px' : EMZI_SPACING.sm,
        backgroundColor: EMZI_COLORS.background
      }}
    >
      <button
        onClick={() => onToggle(number)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '10px' : EMZI_SPACING.compact,
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : EMZI_SPACING.sm }}>
          <div
            style={{
              width: isMobile ? '20px' : '24px',
              height: isMobile ? '20px' : '24px',
              borderRadius: '50%',
              backgroundColor: hasErrors ? EMZI_COLORS.error : EMZI_COLORS.primary,
              color: EMZI_COLORS.textWhite,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600'
            }}
          >
            {number}
          </div>
          <span style={{ ...EMZI_TYPOGRAPHY.h2, fontSize: isMobile ? '14px' : '15px', fontWeight: '600' }}>
            {title}
          </span>
        </div>
        <span style={{ fontSize: isMobile ? '16px' : '18px', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div style={{ padding: isMobile ? '10px' : EMZI_SPACING.compact, paddingTop: 0 }}>
          {children}
        </div>
      )}
    </div>
  );
});

const KnowledgePackWizardCompact = () => {
  const navigate = useNavigate();
  const { packId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, isExecutor, isManagement, isSupervisor, isAdmin } = useAuth();
  
  // Get type from URL parameter (from dashboard click)
  const preSelectedType = searchParams.get('type');

  // iOS Debug Mode (toggle via localStorage)
  const [iosDebugMode] = useState(() => localStorage.getItem('ios_debug_mode') === 'true');
  
  // Render counter for debug mode
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  
  useEffect(() => {
    if (iosDebugMode) {
      console.log(`[WizardCompact] Render #${renderCountRef.current}`);
    }
  });
  
  // Accordion state
  const [openSection, setOpenSection] = useState(1);
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Auto-save state (every 10s while form is open with valid minimum data)
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const autoSavedDraftIdRef = useRef(null); // mirrors packId for stale-closure-free interval

  // Form data
  const [formData, setFormData] = useState({
    pack_type: preSelectedType || 'product',
    name: { en: '', ms: '' },
    description: { en: '', ms: '' },
    visibility: 'public',
    allowed_studios: [],
    allowed_groups: [],
    tags: [],
    content: {},
    relationships: {
      parent_pack_id: null,
      child_pack_ids: [],
      linked_packs: [],
      values_pack_id: '',
      compliance_pack_id: '',
    },
    compliance_profile: {
      level: 'medium',
      restricted_phrases: [],
      safe_alternatives: {},
      substitution_rules: [],
      safe_examples: [],
      warnings: [],
    },
    ai_context: {
      base_weight: 0.5,
      recommended_combinations: [],
      conflict_warning: [],
      tone_influence: {
        formal: 0.0,
        casual: 0.0,
        spiritual: 0.0,
        promotional: 0.0,
        educational: 0.0,
      },
      soa_stage: [],
    },
    version: '1.0.0',
    status: 'draft',
  });

  // Available packs for relationship picker
  const [availablePacks, setAvailablePacks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // AI Assist state
  const [activeRightPanel, setActiveRightPanel] = useState('checklist'); // 'checklist', 'ai', or 'preview'
  
  // Inline Pack Creator state
  const [showInlinePackCreator, setShowInlinePackCreator] = useState(false);
  const [selectedRelationshipType, setSelectedRelationshipType] = useState('requires');
  
  // Completeness score state
  const [completionScore, setCompletionScore] = useState(0);

  useEffect(() => {
    if (packId) {
      setIsEditMode(true);
      loadPack();
    }
  }, [packId]);

  // Handle AI suggestions application
  const handleApplyAISuggestion = (action, value, result) => {
    toast.success(`AI ${action} applied successfully!`);
    // The specific handling depends on the action
    // For now, this is a placeholder that components can use
    console.log('AI Suggestion Applied:', { action, value, result });
  };

  const loadPack = async () => {
    try {
      setIsLoading(true);
      const response = await getPackV3(packId);
      
      if (response.success && response.pack) {
        const pack = response.pack;
        console.log('[WizardCompact] Loading pack:', pack.pack_id, 'Type:', pack.pack_type);
        
        const updatedFormData = {
          pack_type: pack.pack_type,
          name: pack.name || { en: '', ms: '' },
          description: pack.description || { en: '', ms: '' },
          visibility: pack.metadata?.visibility || 'public',
          allowed_studios: pack.metadata?.allowed_studios || [],
          allowed_groups: pack.metadata?.allowed_groups || [],
          tags: pack.metadata?.tags || [],
          content: pack.content || {},
          relationships: pack.relationships || formData.relationships,
          compliance_profile: pack.compliance_profile || formData.compliance_profile,
          ai_context: pack.ai_context || formData.ai_context,
          status: pack.status || pack.metadata?.status || 'draft',
          version: pack.version || '1.0.0',
        };
        
        console.log('[WizardCompact] Setting formData with pack_type:', updatedFormData.pack_type);
        setFormData(updatedFormData);
        
        // Show success toast
        toast.success(`Loaded ${pack.pack_type} pack successfully`);
      } else {
        throw new Error('Invalid pack data received');
      }
    } catch (err) {
      console.error('[WizardCompact] Failed to load pack:', err);
      toast.error(err.message || 'Failed to load pack');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = useCallback((path, value) => {
    if (iosDebugMode) {
      console.log(`[WizardCompact] handleFieldChange called - path: ${path}, value:`, value);
    }
    
    setFormData((prev) => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      
      if (iosDebugMode) {
        console.log(`[WizardCompact] formData updated for path: ${path}`);
      }
      
      return newData;
    });
  }, [iosDebugMode]);

  const handleFieldBlur = useCallback((field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Handler for inline pack creation
  const handleInlinePackCreated = useCallback((newRelationship) => {
    const existingLinks = formData.relationships?.linked_packs || [];
    handleFieldChange('relationships.linked_packs', [...existingLinks, newRelationship]);
  }, [formData.relationships?.linked_packs, handleFieldChange]);

  const validateSection = (section) => {
    const errors = {};
    
    if (section === 1 || section === 2) {
      // Use comprehensive smart validation
      const validation = validateKnowledgePackForm(formData, touchedFields);
      if (!validation.valid) {
        return validation.errors;
      }
    }
    
    // Sections 3 & 4 are optional
    
    return errors;
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const payload = buildPayload('draft');
      console.log('[WizardCompact] Saving draft:', { isEditMode, packId, pack_type: payload.pack_type });
      
      if (isEditMode) {
        const response = await updatePackV3(packId, payload);
        console.log('[WizardCompact] Draft update response:', response);
        // Same migration path as handleCreatePack: if a new version was
        // spawned, follow it so reloads show fresh content.
        const newPackId = response?.pack?.pack_id;
        const newVersion = response?.pack?.version;
        if (newPackId && newPackId !== packId) {
          toast.success(`✅ Version ${newVersion || 'new'} saved as draft`);
          navigate(`/knowledge-packs/wizard/${newPackId}`, { replace: true });
        } else {
          toast.success(`✅ Draft saved - ${payload.pack_type} pack updated`);
          await loadPack();
        }
      } else {
        const response = await createPackV3(payload);
        console.log('[WizardCompact] Draft create response:', response);
        toast.success(`✅ Draft saved - ${payload.pack_type} pack created`);
        if (response.pack_id) {
          // Navigate to edit mode for the newly created pack
          navigate(`/knowledge-packs/wizard/${response.pack_id}`);
        }
      }
    } catch (error) {
      console.error('[WizardCompact] Failed to save draft:', error);
      toast.error(error.message || 'Failed to save draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreatePack = async () => {
    // Validate all sections
    const errors1 = validateSection(1);
    const errors2 = validateSection(2);
    
    if (Object.keys(errors1).length > 0 || Object.keys(errors2).length > 0) {
      console.log('[WizardCompact] Validation errors:', { errors1, errors2 });
      toast.error('Please fix all errors before publishing');
      setTouchedFields({
        ...touchedFields,
        ...Object.keys(errors1).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
        ...Object.keys(errors2).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildPayload('published');
      console.log('[WizardCompact] Publishing pack:', { isEditMode, packId, pack_type: payload.pack_type });
      
      if (isEditMode) {
        const response = await updatePackV3(packId, payload);
        console.log('[WizardCompact] Publish update response:', response);
        // Backend may create a NEW VERSION doc (with a new pack_id) when
        // we update an already-published pack. If that happens, the URL
        // still points to the OLD packId — reloading from there shows
        // stale content (the just-added linked packs / relationships are
        // on the NEW pack_id, not the old one). Navigate to the new doc.
        const newPackId = response?.pack?.pack_id;
        const newVersion = response?.pack?.version;
        if (newPackId && newPackId !== packId) {
          toast.success(`✅ Version ${newVersion || 'new'} created and published`);
          navigate(`/knowledge-packs/wizard/${newPackId}`, { replace: true });
        } else {
          toast.success(`✅ Pack published - ${payload.pack_type} pack is now live`);
          await loadPack();
        }
      } else {
        const response = await createPackV3(payload);
        console.log('[WizardCompact] Publish create response:', response);
        toast.success(`✅ Pack published - ${payload.pack_type} pack created and live`);
        if (response.pack_id) {
          // Navigate to the pack list
          navigate('/knowledge-packs');
        }
      }
    } catch (error) {
      console.error('[WizardCompact] Failed to publish pack:', error);
      toast.error(error.message || 'Failed to publish pack. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    // Validate all sections
    const errors1 = validateSection(1);
    const errors2 = validateSection(2);
    
    if (Object.keys(errors1).length > 0 || Object.keys(errors2).length > 0) {
      toast.error('Please fix all errors before submitting for approval');
      setTouchedFields({
        ...touchedFields,
        ...Object.keys(errors1).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
        ...Object.keys(errors2).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      });
      return;
    }

    setIsSaving(true);
    try {
      // Save as draft first
      const payload = buildPayload('draft');
      const response = await createPackV3(payload);
      const packId = response.pack_id;

      // Create approval request in inbox
      const packName = formData.name.en || formData.name.ms || 'New Pack';
      await inboxApi.createApprovalRequest({
        type: 'approval',
        subject: `Approval Needed: ${formData.pack_type} - ${packName}`,
        message: `I've submitted a new ${formData.pack_type} pack for your review.\n\nPack Name: ${packName}\nDescription: ${formData.description.en || formData.description.ms || 'No description'}\n\nPlease review and approve when ready.`,
        to_user_ids: [], // Backend will auto-assign to supervisors
        related_item: {
          id: packId,
          type: 'knowledge_pack',
          name: packName,
          link: `/knowledge-packs/${packId}`
        },
        priority: 'normal',
        tags: ['knowledge_pack', formData.pack_type]
      });

      toast.success('✅ Submitted for approval! Your supervisor will be notified.');
      navigate('/communications?tab=inbox');
    } catch (error) {
      console.error('Failed to submit for approval:', error);
      toast.error(error.message || 'Failed to submit for approval');
    } finally {
      setIsSaving(false);
    }
  };

  // ----------------------------------------------------------------
  // AUTO-SAVE DRAFT (every 10s)
  // ----------------------------------------------------------------
  // Silent equivalent of "Save Draft":
  //   • Skips when currently saving manually, missing the bare-minimum
  //     name field, or when editing an already-PUBLISHED pack.
  //   • First successful auto-save captures the server pack_id; subsequent
  //     ticks update the SAME pack via PUT — no duplicates.
  //   • Uses a ref to dodge stale-closure inside setInterval.
  const autoSaveDraft = async () => {
    if (isSaving) return;
    if (autoSaveStatus === 'saving') return;
    // Don't auto-save while a published pack is being edited (it has its
    // own versioning flow on update). formData stores status at ROOT.
    if (isEditMode && formData.status === 'published') return;
    // Minimum: at least one of name.en or name.ms must be filled
    const hasName = (formData.name?.en && formData.name.en.trim()) || (formData.name?.ms && formData.name.ms.trim());
    if (!hasName) return;

    try {
      setAutoSaveStatus('saving');
      const payload = buildPayload('draft');
      payload.change_summary = 'Auto-saved draft';
      const targetId = autoSavedDraftIdRef.current || (isEditMode ? packId : null);
      if (targetId) {
        await updatePackV3(targetId, payload);
      } else {
        const resp = await createPackV3(payload);
        const newId = resp?.pack_id || resp?.pack?.pack_id;
        if (newId) {
          autoSavedDraftIdRef.current = newId;
        }
      }
      setLastAutoSavedAt(new Date());
      setAutoSaveStatus('saved');
    } catch (err) {
      console.warn('[WizardCompact] auto-save failed (silent):', err?.message);
      setAutoSaveStatus('error');
    }
  };

  // Run autoSaveDraft every 10s — single mount-time interval that reads
  // the latest state via a ref. Avoids tearing down on every keystroke.
  const autoSaveDraftRef = useRef(autoSaveDraft);
  useEffect(() => { autoSaveDraftRef.current = autoSaveDraft; });
  useEffect(() => {
    const id = setInterval(() => {
      if (autoSaveDraftRef.current) autoSaveDraftRef.current();
    }, 10000);
    return () => clearInterval(id);
  }, []);

  const buildPayload = (status) => {
    return {
      pack_type: formData.pack_type,
      version: formData.version,
      name: formData.name,
      description: formData.description,
      content: formData.content,
      relationships: formData.relationships,
      compliance_profile: formData.compliance_profile,
      ai_context: formData.ai_context,
      metadata: {
        status: status,
        visibility: formData.visibility,
        allowed_studios: formData.allowed_studios,
        allowed_groups: formData.allowed_groups,
        tags: formData.tags,
      },
    };
  };

  // Search packs for relationship picker
  const searchPacks = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
            const response = await fetch(
        `${BACKEND_URL}/api/v3/packs?search=${encodeURIComponent(query)}&limit=10&status=published`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.packs || []);
      }
    } catch (err) {
      console.error('Failed to search packs:', err);
    } finally {
      setIsSearching(false);
    }
  };

  // AccordionSection toggle handler - stable reference
  const handleAccordionToggle = useCallback((number) => {
    setOpenSection(prev => prev === number ? 0 : number);
    setTouchedFields(prev => ({ ...prev, [`section${number}`]: true }));
  }, []);

  const errors1 = validateSection(1);
  const errors2 = validateSection(2);

  // Field refs for jump-to functionality
  const fieldRefs = useRef({});
  
  const handleJumpToField = (fieldPath) => {
    // Jump to the appropriate section based on field path
    if (fieldPath.startsWith('name') || fieldPath.startsWith('description')) {
      setOpenSection(1);
    } else if (fieldPath.startsWith('content')) {
      setOpenSection(2);
    } else if (fieldPath.startsWith('relationships')) {
      setOpenSection(3);
    }
    
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [fieldPath]: true }));
    
    // Scroll to section after a brief delay
    setTimeout(() => {
      const sectionElement = document.getElementById(`section-${openSection}`);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div 
      style={{ 
        backgroundColor: EMZI_COLORS.background,
        height: '100vh',  // Changed from minHeight to height to enable scrolling
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',  // Prevent body scroll
      }}
    >
      {/* Header */}
      <div 
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: EMZI_COLORS.background,
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          padding: EMZI_SPACING.compact,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          flexShrink: 0,
        }}
      >
        {/* Back button */}
        <button
          onClick={() => navigate('/knowledge-packs')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: EMZI_SPACING.xs,
            padding: `${EMZI_SPACING.xs} ${EMZI_SPACING.sm}`,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: EMZI_COLORS.textSecondary,
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: EMZI_SPACING.xs,
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = EMZI_COLORS.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = EMZI_COLORS.textSecondary}
        >
          ← Back to Knowledge Packs
        </button>

        {/* Title */}
        <h1 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: EMZI_COLORS.textPrimary,
          margin: 0,
        }}>
          {isEditMode ? 'Edit Knowledge Pack' : 'Create Knowledge Pack'}
        </h1>
      </div>

      {/* Main Content Container - Scrollable */}
      <div 
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          padding: EMZI_SPACING.compact,
          paddingBottom: window.innerWidth <= 768 ? '140px' : '80px',
          position: 'relative',  // For proper stacking
        }}
      >
        {/* Main Content with 2-Column Layout (Desktop) */}
        <div style={{ 
          display: 'flex',
          gap: EMZI_SPACING.md,
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
        }}>
          {/* Left Column: Form Sections */}
          <div style={{ 
            flex: '1 1 auto',
            minWidth: 0,
            maxWidth: window.innerWidth > 768 ? 'calc(100% - 350px)' : '100%',
          }}>
          {/* Section 1: Basic Info */}
          <AccordionSection number={1} title="Basic Information" isOpen={openSection === 1} onToggle={handleAccordionToggle} hasErrors={Object.keys(errors1).length > 0 && touchedFields['section1']}>
          <BasicInfoSection 
            formData={formData}
            handleFieldChange={handleFieldChange}
            handleFieldBlur={handleFieldBlur}
            errors={errors1}
            touched={touchedFields}
            iosDebugMode={iosDebugMode}
            isLoading={isLoading}
          />
        </AccordionSection>

        {/* Section 2: Content */}
        <AccordionSection number={2} title={`${formData.pack_type.charAt(0).toUpperCase() + formData.pack_type.slice(1)} Content`} isOpen={openSection === 2} onToggle={handleAccordionToggle} hasErrors={Object.keys(errors2).length > 0 && touchedFields['section2']}>
          <ContentSection 
            formData={formData}
            handleFieldChange={handleFieldChange}
            handleFieldBlur={handleFieldBlur}
            errors={errors2}
            touched={touchedFields}
            iosDebugMode={iosDebugMode}
            packId={packId}
            user={user}
          />
        </AccordionSection>

        {/* Section 3: Relationships (Optional) */}
        <AccordionSection number={3} title="Pack Relationships" isOpen={openSection === 3} onToggle={handleAccordionToggle} hasErrors={false}>
          <RelationshipsSection 
            formData={formData}
            handleFieldChange={handleFieldChange}
            searchResults={searchResults}
            searchPacks={searchPacks}
            isSearching={isSearching}
            onQuickCreate={() => setShowInlinePackCreator(true)}
            relationshipType={selectedRelationshipType}
            onRelationshipTypeChange={setSelectedRelationshipType}
          />
        </AccordionSection>

        {/* Section 4: Settings (Optional) */}
        <AccordionSection number={4} title="Settings & AI Context" isOpen={openSection === 4} onToggle={handleAccordionToggle} hasErrors={false}>
          <SettingsSection 
            formData={formData}
            handleFieldChange={handleFieldChange}
          />
        </AccordionSection>
        </div>

        {/* Right Column: Completeness Checklist + AI Assist Panel (Desktop Only) */}
        {window.innerWidth > 768 && (
          <div style={{
            flex: '0 0 320px',
            minWidth: '320px',
            display: 'flex',
            flexDirection: 'column',
            gap: EMZI_SPACING.sm,
          }}>
            {/* Tab Switcher */}
            <div style={{
              display: 'flex',
              gap: '4px',
              padding: '4px',
              backgroundColor: EMZI_COLORS.surface,
              borderRadius: '8px',
            }}>
              <button
                onClick={() => setActiveRightPanel('checklist')}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  backgroundColor: activeRightPanel === 'checklist' ? EMZI_COLORS.background : 'transparent',
                  color: activeRightPanel === 'checklist' ? EMZI_COLORS.textPrimary : EMZI_COLORS.textSecondary,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: activeRightPanel === 'checklist' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                ✓ Checklist
              </button>
              <button
                onClick={() => setActiveRightPanel('ai')}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  backgroundColor: activeRightPanel === 'ai' ? EMZI_COLORS.background : 'transparent',
                  color: activeRightPanel === 'ai' ? EMZI_COLORS.textPrimary : EMZI_COLORS.textSecondary,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: activeRightPanel === 'ai' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                ✨ AI
              </button>
              <button
                onClick={() => setActiveRightPanel('preview')}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  backgroundColor: activeRightPanel === 'preview' ? EMZI_COLORS.background : 'transparent',
                  color: activeRightPanel === 'preview' ? EMZI_COLORS.textPrimary : EMZI_COLORS.textSecondary,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: activeRightPanel === 'preview' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                👁️ Preview
              </button>
            </div>

            {/* Panel Content */}
            {activeRightPanel === 'checklist' ? (
              <CompletenessChecklist
                packType={formData.pack_type}
                formData={formData}
                onJumpToField={handleJumpToField}
                onScoreChange={setCompletionScore}
              />
            ) : activeRightPanel === 'ai' ? (
              <AIAssistPanel
                packType={formData.pack_type}
                packData={formData}
                onApplySuggestion={handleApplyAISuggestion}
                isMobile={false}
              />
            ) : (
              <LiveAgentPreview
                packType={formData.pack_type}
                formData={formData}
                isMobile={false}
              />
            )}
          </div>
        )}

        {/* Mobile: AI Assist FAB */}
        {window.innerWidth <= 768 && (
          <>
            <AIAssistPanel
              packType={formData.pack_type}
              packData={formData}
              onApplySuggestion={handleApplyAISuggestion}
              isMobile={true}
            />
            <LiveAgentPreview
              packType={formData.pack_type}
              formData={formData}
              isMobile={true}
            />
          </>
        )}
        </div>
      </div>

      {/* Sticky Bottom Bar — sits ABOVE the app's BottomNavBar (56px) so the
          Save Draft / Create Pack buttons stay visible on mobile. */}
      <div 
        style={{
          position: 'fixed',
          bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
          left: 0,
          right: 0,
          backgroundColor: EMZI_COLORS.background,
          borderTop: `1px solid ${EMZI_COLORS.border}`,
          padding: EMZI_SPACING.compact,
          display: 'flex',
          gap: EMZI_SPACING.sm,
          alignItems: 'center',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
          zIndex: 20
        }}
        data-testid="wizard-action-bar"
      >
        {/* Auto-save indicator — slim line on the left */}
        <div
          data-testid="wizard-autosave-indicator"
          style={{
            fontSize: '10px',
            color: EMZI_COLORS.textSecondary,
            minWidth: '70px',
            fontStyle: 'italic',
            lineHeight: 1.2,
          }}
        >
          {autoSaveStatus === 'saving' && <span style={{ color: '#0e67a2' }}>💾 Saving…</span>}
          {autoSaveStatus === 'saved' && lastAutoSavedAt && (
            <span style={{ color: '#2E7D32' }}>✓ Auto-saved<br/>{lastAutoSavedAt.toLocaleTimeString()}</span>
          )}
          {autoSaveStatus === 'error' && <span style={{ color: '#C62828' }}>⚠ Auto-save failed</span>}
          {autoSaveStatus === 'idle' && <span style={{ opacity: 0.6 }}>Auto-save<br/>every 10s</span>}
        </div>
        <button
          onClick={handleSaveDraft}
          disabled={isSaving}
          data-testid="wizard-save-draft-btn"
          style={{
            ...getButtonStyle('outlined'),
            flex: 1,
            fontSize: '13px',
            minHeight: '40px'
          }}
        >
          {isSaving ? 'Saving...' : 'Save Draft'}
        </button>
        <div style={{ flex: 2, position: 'relative' }}>
          {/* Show "Create Pack" for Management/Admin/Supervisor, "Submit for Approval" for Executor */}
          {(isManagement() || isAdmin() || isSupervisor()) ? (
            <button
              onClick={handleCreatePack}
              disabled={isSaving || completionScore < 90}
              style={{
                ...getButtonStyle('cta'),
                width: '100%',
                fontSize: '13px',
                minHeight: '40px',
                opacity: completionScore < 90 ? 0.6 : 1,
                cursor: completionScore < 90 ? 'not-allowed' : 'pointer',
              }}
              title={completionScore < 90 ? `Complete required fields to 90% (currently ${completionScore}%)` : ''}
            >
              {isSaving ? 'Creating...' : isEditMode ? 'Update Pack' : 'Create Pack'}
            </button>
          ) : isExecutor() ? (
            <button
              onClick={handleSubmitForApproval}
              disabled={isSaving || completionScore < 90}
              style={{
                ...getButtonStyle('cta'),
                width: '100%',
                fontSize: '13px',
                minHeight: '40px',
                opacity: completionScore < 90 ? 0.6 : 1,
                cursor: completionScore < 90 ? 'not-allowed' : 'pointer',
                backgroundColor: '#10B981', // Green for approval
              }}
              title={completionScore < 90 ? `Complete required fields to 90% (currently ${completionScore}%)` : ''}
            >
              {isSaving ? 'Submitting...' : '✅ Submit for Approval'}
            </button>
          ) : null}
          {completionScore < 90 && !isSaving && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: '8px',
              padding: '6px 12px',
              backgroundColor: EMZI_COLORS.textPrimary,
              color: '#FFFFFF',
              fontSize: '11px',
              borderRadius: EMZI_RADIUS.sm,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              opacity: 0.9,
              zIndex: 1000,
            }}>
              📊 {completionScore}% complete • Need ≥90% to {isExecutor() ? 'submit for approval' : 'create pack'}
            </div>
          )}
        </div>
      </div>

      {/* Inline Pack Creator Modal */}
      <InlinePackCreator
        isOpen={showInlinePackCreator}
        onClose={() => setShowInlinePackCreator(false)}
        onPackCreated={handleInlinePackCreated}
        relationshipType={selectedRelationshipType}
      />
    </div>
  );
};

// ============================================================================
// HELPER: Pack-Specific Basic Info Fields
// ============================================================================
const renderPackSpecificFields = (formData, handleFieldChange, handleFieldBlur, isMobile, iosDebugMode) => {
  const packType = formData.pack_type;
  const content = formData.content || {};

  switch (packType) {
    case 'ambassador':
      return (
        <>
          {/* Ambassador Name */}
          <div>
            <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
              Ambassador Name <span style={{ color: EMZI_COLORS.error }}>*</span>
            </label>
            <ControlledInput
              value={content.ambassador_name || ''}
              onChange={(newValue) => handleFieldChange('content.ambassador_name', newValue)}
              onBlur={() => handleFieldBlur('ambassador_name')}
              placeholder="e.g., Ustaz Kazim Elias"
              debugMode={iosDebugMode}
              fieldName="ambassador_name"
              style={{
                width: '100%',
                minHeight: isMobile ? '36px' : '40px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                fontSize: '14px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                backgroundColor: EMZI_COLORS.background
              }}
            />
          </div>

          {/* Role/Title */}
          <div>
            <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
              Role/Title <span style={{ color: EMZI_COLORS.error }}>*</span>
            </label>
            <ControlledInput
              value={content.role_title || ''}
              onChange={(newValue) => handleFieldChange('content.role_title', newValue)}
              onBlur={() => handleFieldBlur('role_title')}
              placeholder="e.g., Brand Ambassador, Health Advocate, EMZI Care Wellness Expert"
              debugMode={iosDebugMode}
              fieldName="role_title"
              style={{
                width: '100%',
                minHeight: isMobile ? '36px' : '40px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                fontSize: '14px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                backgroundColor: EMZI_COLORS.background
              }}
            />
          </div>

          {/* Voice/Tone */}
          <div>
            <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
              Voice/Tone <span style={{ color: EMZI_COLORS.error }}>*</span>
            </label>
            <ControlledInput
              value={content.voice_tone || ''}
              onChange={(newValue) => handleFieldChange('content.voice_tone', newValue)}
              onBlur={() => handleFieldBlur('voice_tone')}
              placeholder="e.g., Warm, Caring, Educational, Faith-Based Wellness Advocate"
              debugMode={iosDebugMode}
              fieldName="voice_tone"
              style={{
                width: '100%',
                minHeight: isMobile ? '36px' : '40px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                fontSize: '14px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                backgroundColor: EMZI_COLORS.background
              }}
            />
          </div>
        </>
      );

    case 'promotion':
      return (
        <>
          {/* Campaign Name */}
          <div>
            <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
              Campaign Name <span style={{ color: EMZI_COLORS.error }}>*</span>
            </label>
            <ControlledInput
              value={content.campaign_name?.en || content.campaign_name || ''}
              onChange={(newValue) => handleFieldChange('content.campaign_name', { en: newValue, ms: content.campaign_name?.ms || '' })}
              onBlur={() => handleFieldBlur('campaign_name')}
              placeholder="e.g., Buy 2 Olivetin Get 1 Free, Ramadan Wellness Bundle"
              debugMode={iosDebugMode}
              fieldName="campaign_name"
              style={{
                width: '100%',
                minHeight: isMobile ? '36px' : '40px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                fontSize: '14px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                backgroundColor: EMZI_COLORS.background
              }}
            />
          </div>

          {/* Offer Type */}
          <div>
            <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
              Offer Type <span style={{ color: EMZI_COLORS.error }}>*</span>
            </label>
            <select
              value={content.offer_type || ''}
              onChange={(e) => handleFieldChange('content.offer_type', e.target.value)}
              style={{
                width: '100%',
                minHeight: isMobile ? '38px' : '40px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                fontSize: '14px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                backgroundColor: EMZI_COLORS.background
              }}
            >
              <option value="">Select offer type...</option>
              <option value="percentage_discount">Percentage Discount</option>
              <option value="fixed_amount">Fixed Amount Off</option>
              <option value="buy_x_get_y">Buy X Get Y</option>
              <option value="bundle">Bundle Deal</option>
              <option value="free_shipping">Free Shipping</option>
              <option value="limited_time">Limited Time Offer</option>
            </select>
          </div>

          {/* Date Range */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '6px' : EMZI_SPACING.sm }}>
            <div>
              <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
                Start Date <span style={{ color: EMZI_COLORS.error }}>*</span>
              </label>
              <input
                type="date"
                value={content.start_date || ''}
                onChange={(e) => handleFieldChange('content.start_date', e.target.value)}
                style={{
                  width: '100%',
                  minHeight: isMobile ? '36px' : '40px',
                  padding: isMobile ? '6px 10px' : '8px 12px',
                  fontSize: '14px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  backgroundColor: EMZI_COLORS.background
                }}
              />
            </div>
            <div>
              <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
                End Date <span style={{ color: EMZI_COLORS.error }}>*</span>
              </label>
              <input
                type="date"
                value={content.end_date || ''}
                onChange={(e) => handleFieldChange('content.end_date', e.target.value)}
                style={{
                  width: '100%',
                  minHeight: isMobile ? '36px' : '40px',
                  padding: isMobile ? '6px 10px' : '8px 12px',
                  fontSize: '14px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  backgroundColor: EMZI_COLORS.background
                }}
              />
            </div>
          </div>
        </>
      );

    case 'health':
      return (
        <>
          {/* Health Topic */}
          <div>
            <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
              Health Topic <span style={{ color: EMZI_COLORS.error }}>*</span>
            </label>
            <ControlledInput
              value={content.health_topic?.en || content.health_topic || ''}
              onChange={(newValue) => handleFieldChange('content.health_topic', { en: newValue, ms: content.health_topic?.ms || '' })}
              onBlur={() => handleFieldBlur('health_topic')}
              placeholder="e.g., Heart & Cholesterol Support, Cardiovascular Wellness, Blood Pressure Management"
              debugMode={iosDebugMode}
              fieldName="health_topic"
              style={{
                width: '100%',
                minHeight: isMobile ? '36px' : '40px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                fontSize: '14px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                backgroundColor: EMZI_COLORS.background
              }}
            />
          </div>

          {/* Health Category */}
          <div>
            <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: isMobile ? '2px' : EMZI_SPACING.xs }}>
              Health Category <span style={{ color: EMZI_COLORS.error }}>*</span>
            </label>
            <select
              value={content.health_category || ''}
              onChange={(e) => handleFieldChange('content.health_category', e.target.value)}
              style={{
                width: '100%',
                minHeight: isMobile ? '38px' : '40px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                fontSize: '14px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                backgroundColor: EMZI_COLORS.background
              }}
            >
              <option value="">Select category...</option>
              <option value="general_health">General Health</option>
              <option value="digestive">Digestive Health</option>
              <option value="cardiovascular">Cardiovascular Health</option>
              <option value="immunity">Immunity</option>
              <option value="bone_joint">Bone & Joint Health</option>
              <option value="weight_management">Weight Management</option>
              <option value="mental_wellbeing">Mental Wellbeing</option>
            </select>
          </div>
        </>
      );

    // Standard packs (Product, Services, Values, Compliance, Direction, Viral, Trend) 
    // use generic Name + Description only
    default:
      return null;
  }
};

// ============================================================================
// SECTION 1: BASIC INFO
// ============================================================================
const BasicInfoSection = React.memo(({ formData, handleFieldChange, handleFieldBlur, errors, touched, iosDebugMode = false, isLoading = false }) => {
  const isBilingual = BILINGUAL_REQUIRED[formData.pack_type];
  const isMobile = window.innerWidth <= 768;
  const { language } = useLanguage(); // Get current language from context
  
  // Helper to get translated text
  const t = (key) => FORM_LABELS[key]?.[language] || FORM_LABELS[key]?.en || key;
  
  // Debug mode indicator
  React.useEffect(() => {
    if (iosDebugMode) {
      console.log('[BasicInfoSection] iOS Debug Mode ENABLED');
      console.log('[BasicInfoSection] Mounted with formData:', formData);
      console.log('[BasicInfoSection] Current language:', language);
    }
  }, [iosDebugMode, language]);
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '6px' : EMZI_SPACING.md }}>
      {/* Pack Type */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: '4px' }}>
          {t('packType')} <span style={{ color: EMZI_COLORS.error }}>*</span>
        </label>
        <select
          value={formData.pack_type || 'product'}
          onChange={(e) => handleFieldChange('pack_type', e.target.value)}
          disabled={isLoading}
          style={{
            width: '100%',
            minHeight: isMobile ? '38px' : '40px',
            padding: isMobile ? '6px 10px' : '8px 12px',
            fontSize: '14px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            backgroundColor: isLoading ? EMZI_COLORS.disabled : EMZI_COLORS.background,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {Object.values(PACK_TYPES).map((type) => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Universal Language Notice */}
      <div style={{
        padding: isMobile ? '6px 10px' : '8px 12px',
        backgroundColor: '#F0F9FF',
        border: '1px solid #BAE6FD',
        borderRadius: EMZI_RADIUS.md,
        fontSize: isMobile ? '11px' : '12px',
        color: '#0369A1',
        lineHeight: '1.5'
      }}>
        {t('universalInputNotice')}
      </div>

      {/* Universal Pack Name Field */}
      <UniversalLanguageField
        label={t('packName')}
        value={formData.name.ms || formData.name.en || ''}
        onChange={(value) => {
          // Store in both fields with a single update for backward compatibility
          handleFieldChange('name', { en: value, ms: value });
        }}
        helper={t('packNameHelper')}
        required={true}
        multiline={false}
        placeholder={t('packNamePlaceholder')}
        onBlur={() => handleFieldBlur('name')}
        hasError={touched.name_en && errors.name_en}
        errorMessage={errors.name_en}
        language={language}
        adaptMessage={t('aiAdaptMessage')}
      />

      {/* Universal Description Field */}
      <UniversalLanguageField
        label={t('description')}
        value={formData.description.ms || formData.description.en || ''}
        onChange={(value) => {
          // Store in both fields with a single update for backward compatibility
          handleFieldChange('description', { en: value, ms: value });
        }}
        helper={t('descriptionHelper')}
        required={true}
        multiline={true}
        rows={3}
        placeholder={t('descriptionPlaceholder')}
        onBlur={() => handleFieldBlur('description')}
        hasError={touched.description && errors.description}
        errorMessage={errors.description}
        language={language}
        adaptMessage={t('aiAdaptMessage')}
      />

      {/* Pack-Type Specific Fields */}
      {renderPackSpecificFields(formData, handleFieldChange, handleFieldBlur, isMobile, iosDebugMode)}

      {/* Visibility */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Visibility <span style={{ color: EMZI_COLORS.error }}>*</span>
        </label>
        <select
          value={formData.visibility}
          onChange={(e) => handleFieldChange('visibility', e.target.value)}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '8px 12px',
            fontSize: '14px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            backgroundColor: EMZI_COLORS.background
          }}
        >
          <option value="public">Public (All Users)</option>
          <option value="group_scoped">Group Scoped</option>
          <option value="admin_only">Admin Only</option>
        </select>
      </div>

      {/* Tags */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Tags
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: EMZI_SPACING.xs, marginBottom: EMZI_SPACING.xs }}>
          {formData.tags.map((tag, idx) => (
            <ChipTag
              key={`item-${idx}`}
              label={tag}
              size="sm"
              onDelete={() => {
                const updated = formData.tags.filter((_, i) => i !== idx);
                handleFieldChange('tags', updated);
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: EMZI_SPACING.xs }}>
          <input
            type="text"
            id="new-tag-input-basic"
            placeholder="Add tag..."
            style={{
              flex: 1,
              minHeight: '40px',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const newTag = e.target.value.trim();
                if (newTag && !formData.tags.includes(newTag)) {
                  handleFieldChange('tags', [...formData.tags, newTag]);
                  e.target.value = '';
                }
              }
            }}
          />
          <button
            type="button"
            onClick={() => {
              const input = document.getElementById('new-tag-input-basic');
              const newTag = input.value.trim();
              if (newTag && !formData.tags.includes(newTag)) {
                handleFieldChange('tags', [...formData.tags, newTag]);
                input.value = '';
              }
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: EMZI_COLORS.accent,
              color: EMZI_COLORS.textWhite,
              border: 'none',
              borderRadius: EMZI_RADIUS.md,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              minHeight: '40px'
            }}
          >
            + Add
          </button>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// SECTION 2: CONTENT (Dynamic by Pack Type)
// ============================================================================
const ContentSection = ({ formData, handleFieldChange, handleFieldBlur, errors, touched, iosDebugMode = false, packId, user }) => {
  const packType = formData.pack_type;
  
  const updateContent = (contentOrKey, maybeValue) => {
    if (maybeValue !== undefined) {
      // Legacy signature: updateContent(key, value) - update single field in content
      handleFieldChange(`content.${contentOrKey}`, maybeValue);
    } else {
      // Enhanced form signature: updateContent(newContentObject) - replace whole content
      handleFieldChange('content', contentOrKey);
    }
  };
  
  const handleContentFieldBlur = (field) => {
    if (handleFieldBlur) {
      handleFieldBlur(field);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.md }}>
      {packType === 'product' && (
        <ProductPackEditor
          packData={formData.content}
          onUpdate={() => {
            // Trigger form validation or re-fetch
            if (handleFieldChange) {
              handleFieldChange('content', formData.content);
            }
          }}
          packId={packId || formData.pack_id}
          userId={user?.user_id || user?.id || ''}
        />
      )}
      {packType === 'promotion' && (
        <EnhancedPromotionForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'direction' && (
        <EnhancedDirectionForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'values' && (
        <EnhancedValuesForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'services' && (
        <EnhancedServicesForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'compliance' && (
        <EnhancedComplianceForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'viral' && (
        <EnhancedViralForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'trend' && (
        <EnhancedTrendForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'ambassador' && (
        <EnhancedAmbassadorForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
          iosDebugMode={iosDebugMode}
        />
      )}
      {packType === 'health' && (
        <EnhancedHealthForm 
          content={formData.content} 
          onChange={updateContent} 
          errors={errors} 
          touched={touched} 
          onFieldBlur={handleContentFieldBlur}
          packType={packType}
        />
      )}
      {packType === 'format' && (
        <FormatContentEditor
          content={formData.content}
          updateContent={updateContent}
        />
      )}
    </div>
  );
};

// Product Content
const ProductContent = ({ content, updateContent, errors, touched, handleFieldBlur }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Features <span style={{ color: EMZI_COLORS.error }}>*</span>
      </label>
      <textarea
        value={content.features || ''}
        onChange={(e) => updateContent('features', e.target.value)}
        onBlur={() => handleFieldBlur('features')}
        placeholder="Enter product features (one per line)..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${touched.features && errors.features ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
      {touched.features && errors.features && (
        <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
          {errors.features}
        </span>
      )}
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Benefits <span style={{ color: EMZI_COLORS.error }}>*</span>
      </label>
      <textarea
        value={content.benefits || ''}
        onChange={(e) => updateContent('benefits', e.target.value)}
        onBlur={() => handleFieldBlur('benefits')}
        placeholder="Enter product benefits (one per line)..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${touched.benefits && errors.benefits ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
      {touched.benefits && errors.benefits && (
        <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
          {errors.benefits}
        </span>
      )}
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Price Range
      </label>
      <input
        type="text"
        value={content.price_range || ''}
        onChange={(e) => updateContent('price_range', e.target.value)}
        placeholder="e.g., RM 50 - RM 100"
        style={{
          width: '100%',
          minHeight: '40px',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background
        }}
      />
    </div>
  </>
);

// Promotion Content
const PromotionContent = ({ content, updateContent, errors, touched, handleFieldBlur }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Offer <span style={{ color: EMZI_COLORS.error }}>*</span>
      </label>
      <input
        type="text"
        value={content.offer || ''}
        onChange={(e) => updateContent('offer', e.target.value)}
        onBlur={() => handleFieldBlur('offer')}
        placeholder="e.g., 20% OFF + Free Shipping"
        style={{
          width: '100%',
          minHeight: '40px',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${touched.offer && errors.offer ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background
        }}
      />
      {touched.offer && errors.offer && (
        <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
          {errors.offer}
        </span>
      )}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: EMZI_SPACING.sm }}>
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Start Date <span style={{ color: EMZI_COLORS.error }}>*</span>
        </label>
        <input
          type="date"
          value={content.start_date || ''}
          onChange={(e) => updateContent('start_date', e.target.value)}
          onBlur={() => handleFieldBlur('start_date')}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '8px 12px',
            fontSize: '13px',
            border: `1px solid ${touched.start_date && errors.start_date ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            backgroundColor: EMZI_COLORS.background
          }}
        />
        {touched.start_date && errors.start_date && (
          <span style={{ fontSize: '10px', color: EMZI_COLORS.error, marginTop: '2px', display: 'block' }}>
            {errors.start_date}
          </span>
        )}
      </div>
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          End Date <span style={{ color: EMZI_COLORS.error }}>*</span>
        </label>
        <input
          type="date"
          value={content.end_date || ''}
          onChange={(e) => updateContent('end_date', e.target.value)}
          onBlur={() => handleFieldBlur('end_date')}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '8px 12px',
            fontSize: '13px',
            border: `1px solid ${touched.end_date && errors.end_date ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            backgroundColor: EMZI_COLORS.background
          }}
        />
        {touched.end_date && errors.end_date && (
          <span style={{ fontSize: '10px', color: EMZI_COLORS.error, marginTop: '2px', display: 'block' }}>
            {errors.end_date}
          </span>
        )}
      </div>
    </div>
  </>
);

// Direction Content
const DirectionContent = ({ content, updateContent, errors, touched, handleFieldBlur }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Strategic Objective <span style={{ color: EMZI_COLORS.error }}>*</span>
      </label>
      <textarea
        value={content.strategic_objective || ''}
        onChange={(e) => updateContent('strategic_objective', e.target.value)}
        onBlur={() => handleFieldBlur('strategic_objective')}
        placeholder="Main strategic objective..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${touched.strategic_objective && errors.strategic_objective ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
      {touched.strategic_objective && errors.strategic_objective && (
        <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
          {errors.strategic_objective}
        </span>
      )}
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Key Messages <span style={{ color: EMZI_COLORS.error }}>*</span>
      </label>
      <textarea
        value={content.key_messages || ''}
        onChange={(e) => updateContent('key_messages', e.target.value)}
        onBlur={() => handleFieldBlur('key_messages')}
        placeholder="Key messages (one per line)..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${touched.key_messages && errors.key_messages ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
      {touched.key_messages && errors.key_messages && (
        <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
          {errors.key_messages}
        </span>
      )}
    </div>
  </>
);

// Values Content
const ValuesContent = ({ content, updateContent }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Core Values
      </label>
      <textarea
        value={content.core_values || ''}
        onChange={(e) => updateContent('core_values', e.target.value)}
        placeholder="Core values (one per line)..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Descriptions
      </label>
      <textarea
        value={content.descriptions || ''}
        onChange={(e) => updateContent('descriptions', e.target.value)}
        placeholder="Value descriptions..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
  </>
);

// Services Content
const ServicesContent = ({ content, updateContent }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Service Name
      </label>
      <input
        type="text"
        value={content.service_name || ''}
        onChange={(e) => updateContent('service_name', e.target.value)}
        placeholder="e.g., Premium Consultation Service"
        style={{
          width: '100%',
          minHeight: '40px',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background
        }}
      />
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Description
      </label>
      <textarea
        value={content.description || ''}
        onChange={(e) => updateContent('description', e.target.value)}
        placeholder="Service description..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
  </>
);

// Compliance Content
const ComplianceContent = ({ content, updateContent, errors, touched, handleFieldBlur }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Regulations & Rules <span style={{ color: EMZI_COLORS.error }}>*</span>
      </label>
      <textarea
        value={content.regulations || ''}
        onChange={(e) => updateContent('regulations', e.target.value)}
        onBlur={() => handleFieldBlur('regulations')}
        placeholder="Regulations (one per line)..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${touched.regulations && errors.regulations ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
      {touched.regulations && errors.regulations && (
        <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
          {errors.regulations}
        </span>
      )}
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Prohibited Terms <span style={{ color: EMZI_COLORS.error }}>*</span>
      </label>
      <textarea
        value={content.prohibited_terms || ''}
        onChange={(e) => updateContent('prohibited_terms', e.target.value)}
        onBlur={() => handleFieldBlur('prohibited_terms')}
        placeholder="Prohibited terms (one per line)..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${touched.prohibited_terms && errors.prohibited_terms ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
      {touched.prohibited_terms && errors.prohibited_terms && (
        <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
          {errors.prohibited_terms}
        </span>
      )}
    </div>
  </>
);

// Viral Content
const ViralContent = ({ content, updateContent }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Viral Theme
      </label>
      <input
        type="text"
        value={content.theme || ''}
        onChange={(e) => updateContent('theme', e.target.value)}
        placeholder="e.g., Challenge, Transformation"
        style={{
          width: '100%',
          minHeight: '40px',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background
        }}
      />
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Hook/Opening
      </label>
      <textarea
        value={content.hook || ''}
        onChange={(e) => updateContent('hook', e.target.value)}
        placeholder="Attention-grabbing opening..."
        rows={2}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
  </>
);

// Trend Content
const TrendContent = ({ content, updateContent }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Trend Name/Topic
      </label>
      <input
        type="text"
        value={content.trend_name || ''}
        onChange={(e) => updateContent('trend_name', e.target.value)}
        placeholder="e.g., Plant-Based Nutrition 2025"
        style={{
          width: '100%',
          minHeight: '40px',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background
        }}
      />
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Market Context
      </label>
      <textarea
        value={content.market_context || ''}
        onChange={(e) => updateContent('market_context', e.target.value)}
        placeholder="What's happening in the market..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
  </>
);

// Health Content
const HealthContent = ({ content, updateContent }) => (
  <>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Health Topic
      </label>
      <input
        type="text"
        value={content.health_topic || ''}
        onChange={(e) => updateContent('health_topic', e.target.value)}
        placeholder="e.g., Heart Health, Immunity, Digestion"
        style={{
          width: '100%',
          minHeight: '40px',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background
        }}
      />
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Scientific Background
      </label>
      <textarea
        value={content.scientific_background || ''}
        onChange={(e) => updateContent('scientific_background', e.target.value)}
        placeholder="Scientific basis and research backing..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Health Benefits
      </label>
      <textarea
        value={content.health_benefits || ''}
        onChange={(e) => updateContent('health_benefits', e.target.value)}
        placeholder="Key health benefits (one per line)..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
    <div>
      <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
        Recommended Usage
      </label>
      <textarea
        value={content.recommended_usage || ''}
        onChange={(e) => updateContent('recommended_usage', e.target.value)}
        placeholder="How to use for best results..."
        rows={2}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: `1px solid ${EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          backgroundColor: EMZI_COLORS.background,
          resize: 'vertical'
        }}
      />
    </div>
  </>
);

// ============================================================================
// SECTION 3: RELATIONSHIPS (Optional)
// ============================================================================
const RelationshipsSection = ({ formData, handleFieldChange, searchResults, searchPacks, isSearching, onQuickCreate, relationshipType: parentRelationshipType, onRelationshipTypeChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [relationshipType, setRelationshipType] = useState(parentRelationshipType || 'requires');
  const [showPicker, setShowPicker] = useState(false);

  // Update parent when relationship type changes
  useEffect(() => {
    if (onRelationshipTypeChange) {
      onRelationshipTypeChange(relationshipType);
    }
  }, [relationshipType, onRelationshipTypeChange]);

  const addRelationship = (pack) => {
    const newRelationship = {
      pack_id: pack.pack_id,
      pack_name: pack.name,
      pack_type: pack.pack_type,
      relationship_type: relationshipType,
      linked_by: 'user',
      linked_at: new Date().toISOString(),
    };

    const existingLinks = formData.relationships?.linked_packs || [];
    const isDuplicate = existingLinks.some(r => r.pack_id === pack.pack_id);

    if (!isDuplicate) {
      handleFieldChange('relationships.linked_packs', [...existingLinks, newRelationship]);
      toast.success('Pack linked');
    } else {
      toast.error('Already linked');
    }

    setSearchQuery('');
    setShowPicker(false);
  };

  const removeRelationship = (packId) => {
    const existingLinks = formData.relationships?.linked_packs || [];
    handleFieldChange(
      'relationships.linked_packs',
      existingLinks.filter(r => r.pack_id !== packId)
    );
    toast.success('Link removed');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.md }}>
      <p style={{ fontSize: '12px', color: EMZI_COLORS.textSecondary, margin: 0 }}>
        Link this pack to other packs to build knowledge hierarchies.
      </p>

      {/* Relationship Type */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Relationship Type
        </label>
        <select
          value={relationshipType}
          onChange={(e) => setRelationshipType(e.target.value)}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '8px 12px',
            fontSize: '14px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            backgroundColor: EMZI_COLORS.background
          }}
        >
          <option value="requires">Requires</option>
          <option value="recommends">Recommends</option>
          <option value="conflicts_with">Conflicts With</option>
          <option value="inherits_from">Inherits From</option>
          <option value="supports">Supports</option>
        </select>
      </div>

      {/* Pack Search */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Search Packs
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchPacks(e.target.value);
            }}
            onFocus={() => setShowPicker(true)}
            placeholder="Type to search..."
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background
            }}
          />
          {isSearching && (
            <div style={{ position: 'absolute', right: '12px', top: '12px', fontSize: '12px', color: EMZI_COLORS.textMuted }}>
              Searching...
            </div>
          )}
        </div>

        {/* Search Results */}
        {showPicker && searchResults.length > 0 && (
          <div style={{
            marginTop: EMZI_SPACING.xs,
            backgroundColor: EMZI_COLORS.background,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {searchResults.map((pack) => (
              <div
                key={pack.pack_id}
                onClick={() => addRelationship(pack)}
                style={{
                  padding: EMZI_SPACING.sm,
                  borderBottom: `1px solid ${EMZI_COLORS.borderLight}`,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = EMZI_COLORS.hoverBg}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ fontSize: '13px', fontWeight: '600', color: EMZI_COLORS.textPrimary }}>
                  {txt(pack.name, 'en', 'Unnamed Pack')}
                </div>
                <div style={{ fontSize: '11px', color: EMZI_COLORS.textMuted }}>
                  {pack.pack_type} • {pack.status || pack.metadata?.status || 'draft'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Create Button */}
        <button
          onClick={onQuickCreate}
          style={{
            marginTop: EMZI_SPACING.xs,
            width: '100%',
            padding: '8px 12px',
            backgroundColor: EMZI_COLORS.surface,
            color: EMZI_COLORS.primary,
            border: `1px dashed ${EMZI_COLORS.primary}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <span style={{ fontSize: '16px' }}>+</span> Quick Create & Link New Pack
        </button>
      </div>

      {/* Linked Packs */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Linked Packs
        </label>
        {formData.relationships?.linked_packs?.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: EMZI_SPACING.xs }}>
            {formData.relationships.linked_packs.map((rel) => (
              <ChipTag
                key={rel.pack_id}
                label={`${rel.pack_name?.en || rel.pack_name || 'Unknown'} (${rel.relationship_type})`}
                size="sm"
                onDelete={() => removeRelationship(rel.pack_id)}
              />
            ))}
          </div>
        ) : (
          <p style={{ fontSize: '12px', color: EMZI_COLORS.textMuted, fontStyle: 'italic', margin: 0 }}>
            No relationships yet
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SECTION 4: SETTINGS & AI CONTEXT (Optional)
// ============================================================================
const SettingsSection = ({ formData, handleFieldChange }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.md }}>
      {/* Compliance Level */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Compliance Level
        </label>
        <select
          value={formData.compliance_profile.level}
          onChange={(e) => handleFieldChange('compliance_profile.level', e.target.value)}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '8px 12px',
            fontSize: '14px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            backgroundColor: EMZI_COLORS.background
          }}
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {/* AI Base Weight */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          AI Context Weight: {formData.ai_context.base_weight}
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={formData.ai_context.base_weight}
          onChange={(e) => handleFieldChange('ai_context.base_weight', parseFloat(e.target.value))}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: EMZI_COLORS.textMuted }}>
          <span>Low Priority</span>
          <span>High Priority</span>
        </div>
      </div>

      {/* Publication Status */}
      <div>
        <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
          Status
        </label>
        <select
          value={formData.status}
          onChange={(e) => handleFieldChange('status', e.target.value)}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '8px 12px',
            fontSize: '14px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            backgroundColor: EMZI_COLORS.background
          }}
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
    </div>
  );
};

export default KnowledgePackWizardCompact;
